import { useContext, useEffect, useRef } from "react";
import "./TextBox.css";
import { FormContext } from "../../contexts/FormContext";
import Helper from "../../util/helper";
import { isMobile } from "react-device-detect";
import { RenderScrollContext } from "../../contexts/RenderScrollContext";

export const TextBox = ({ isAdvancedForm, componentData, onUpdateValue, isHorizontal, currentSection }) => {
  const { isHTML, checkDisabled, getScreenDimensions } = useContext(FormContext);
  const { calculateWidthPercentageOfPage, getPageWidth, getPageHeight } = useContext(RenderScrollContext);
  
  const isDisabled = checkDisabled(componentData, isAdvancedForm);
  let hasError = componentData.error;
  const hasMaxlines = componentData.maxlines > 1;
  const hasMaxLength = !!componentData.maxlength;

  let inputValue = '';

  if (isAdvancedForm) {
    if (isHTML) {
      inputValue = componentData.response.text.split(">")[1]?.split("<")[0];
    }
    inputValue = componentData.response.text;
  } else {
    if (isHTML) {
      inputValue = componentData.response.split(">")[1]?.split("<")?.[0];
    } else {
      inputValue = componentData.response;
    }
  }

  const title = isAdvancedForm ? componentData.title.disableprintpdf ? '' : componentData.title.text : componentData.title;

  const lettersCounter = inputValue?.length;

  const inputRef = useRef(null);

  const getHeightStyle = () => {
    return componentData.maxlines * 15 + 5 + "px";
  };

  const getHTMLStyles = () => {
    let sentStyles;

    if (isAdvancedForm) {
      sentStyles = componentData.response.text !== null ? componentData.response.text.split('style="')[1]?.split('"')[0]: null;
    } else {
      sentStyles = componentData.response.split('style="')[1]?.split('"')[0]; 
    }

    return sentStyles ? `${sentStyles}; height: ${getHeightStyle()}` : `height: ${getHeightStyle()}`;
  }

  useEffect(() => {
    if (!isAdvancedForm && isHTML) {
      const styleString = getHTMLStyles();

      inputRef.current.setAttribute('style', styleString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When input is rendered and contains a pre-loaded value from a previous section, we need to erase it and leave it empty
    if (!isAdvancedForm) {
      const preLoadedValue = inputRef?.current?.value;

      // If the input was previously filled and now the user navigated back to this section, we need to recover the value
      if (preLoadedValue && !componentData.response) {
        inputRef.current.value = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection]);

  useEffect(() => {
    if(isDisabled) {
      onUpdateValue(isAdvancedForm ? {
        ...componentData.response,
        text:''
      }: '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled])

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (hasMaxLength && newValue.length > componentData.maxlength) return;
    onUpdateValue(isAdvancedForm ? {
      ...componentData.response,
      text:newValue
    } : newValue);
  };

  

  const onTextAreaChange = (e) => {
    handleChange(e);
  };

  const getTitleStyles = () => {
    const { fontFamily, fontSize } = getScreenDimensions();

    const { posx, posy, sizex, page } = componentData.title.position;
    const pageWidth = getPageWidth(page - 1);
    const pageHeight = getPageHeight(page - 1);
    const percentage = calculateWidthPercentageOfPage(page - 1);
    return Helper.getElementPositionStyles(posx, posy, sizex, fontFamily, '', pageWidth, pageHeight, percentage, isHorizontal, fontSize);
  }

  const getResponseStyles = () => {
    const { fontFamily, fontSize } = getScreenDimensions();

    const { posx, posy, sizex, page } = componentData.response.position;
    const pageWidth = getPageWidth(page - 1);
    const pageHeight = getPageHeight(page - 1);
    const percentage = calculateWidthPercentageOfPage(page - 1);
    const positionStyles = Helper.getElementPositionStyles(posx, posy, sizex, fontFamily, '', pageWidth, pageHeight, percentage, isHorizontal, fontSize);

    const styleString = getHTMLStyles();

    const parsedStyles = Helper.getStyleObjectFromString(styleString);

    return {
      ...positionStyles,
      ...parsedStyles,
    };
  }

  return (
    <div className={`${isDisabled ? "disabled-container" : ""} ${hasError && !isAdvancedForm ? "error-container" : "" } ${hasError && isAdvancedForm ? "text-error-container" : "" } ${isAdvancedForm ? 'advanced-form' : ''} textbox-container`}>
      {isHTML ? (
        <div
          style={isAdvancedForm ? getTitleStyles() : {}}
          dangerouslySetInnerHTML={Helper.mapInnerHtmlData(title)}
        />
      ) : (
        <label style={isAdvancedForm ? getTitleStyles() : {}}>{title}</label>
      )}
      {hasMaxlines ? (
        <textarea
          style={isAdvancedForm ? getResponseStyles() : {}}
          disabled={isDisabled}
          maxLength={hasMaxLength ? componentData.maxlength : undefined}
          className="textarea-form"
          onChange={onTextAreaChange}
          value={inputValue}
          ref={inputRef}
        />
      ) : (
        <input
          style={{
            ...isAdvancedForm ? getResponseStyles() : {},
            ...(isAdvancedForm ? { border: '1px solid black' } : {}),
            ...(isAdvancedForm && isMobile ? { height: '10px' } : {}),
          }}
          disabled={isDisabled}
          type="text"
          className="text-input"
          maxLength={hasMaxLength ? componentData.maxlength : undefined}
          onChange={handleChange}
          value={inputValue}
          ref={inputRef}
        ></input>
      )}
      {hasMaxLength && (
        <div className="letters-counter">
          {lettersCounter}/{componentData.maxlength}
        </div>
      )}
    </div>
  );
};
